import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon, type IconName } from '@/components/kbIcons';
import { actesReference, actesBoard, type ActesBoard, type ActesReference } from '@/integrations/actesApi';
import { friendlyError } from '@/lib/friendlyError';
import { logPortalError } from '@/integrations/errorLog';
import { takeActesFocus, onActesFocus } from '@/lib/actesFocus';
import { DossiersPanel } from './DossiersPanel';
import { DemandesPanel } from './DemandesPanel';
import { BoardPanel } from './BoardPanel';
import { DossierDetail } from './DossierDetail';
import { ReceptionPanel } from './ReceptionPanel';
import { DpcPanel } from './DpcPanel';
import { AuthoritiesPanel } from './AuthoritiesPanel';
import { TrashPanel } from './TrashPanel';
import { GuidePanel } from './GuidePanel';
import { UndoToaster } from './UndoToaster';
import { actesTabsFor, useActesCountry, type ActesCountry, actesEn } from '@/lib/actesCountry';

/**
 * FRANCE · GESTION DES ACTES, as a section of the portal.
 *
 * It used to be its own site with its own left sidebar, and the two navigations
 * would have fought: a rail of portal sections beside a rail of Actes pages,
 * neither of them saying which world you were in. So the section carries its
 * OWN bar — horizontal, at the top, named — and asks the portal's sidebar to
 * step aside while you are here. One rail, one meaning: the left one is the
 * portal, the top one is Actes.
 *
 * Only France sees it, and only the people the Actes schema knows.
 */

/** A network drop or a server hiccup — worth another try. A refusal (403) or
 *  a real answer is not. */
const isTransient = (err: unknown): boolean =>
  err instanceof TypeError || /failed to fetch|networkerror|load failed|HTTP 5\d\d/i.test(err instanceof Error ? err.message : String(err));

export type ActesTab = 'board' | 'dossiers' | 'demandes' | 'dpc' | 'authorities' | 'reception' | 'guide' | 'trash';

const TABS: { id: ActesTab; icon: IconName; fr: string; en: string; adminOnly?: boolean }[] = [
  { id: 'board', icon: 'grid', fr: 'Tableau de bord', en: 'Dashboard' },
  { id: 'dossiers', icon: 'documents', fr: 'Dossiers', en: 'Dossiers' },
  { id: 'demandes', icon: 'tasks', fr: 'Demandes', en: 'Requests' },
  { id: 'dpc', icon: 'hourglass', fr: 'Compléments', en: 'DPC' },
  { id: 'authorities', icon: 'building', fr: 'Autorités', en: 'Authorities' },
  /* Ouverte à toute l'équipe France : un scan ne doit pas attendre qu'un
     super admin soit libre. Qui l'a déposé est enregistré sur l'envoi. */
  { id: 'reception', icon: 'ai', fr: 'Réception (IA)', en: 'Intake (AI)' },
  /* Rien n'est détruit : la corbeille est le chemin du retour. */
  /* The guide sits last but one, before the bin: it is what you open when
     you do not know where to click, not something you use every day. */
  { id: 'guide', icon: 'logbook', fr: 'Guide', en: 'Guide' },
  { id: 'trash', icon: 'trash', fr: 'Corbeille', en: 'Trash', adminOnly: true },
];

/* WHAT IS DELIBERATELY NOT HERE. The old site had pages for announcements,
   notifications and administration. The portal already has all three, so
   duplicating them would have been the opposite of a fusion:
     · notifications — every Actes notification is mirrored into the portal's
       own bell as it is written (a trigger on actes.notifications), so they
       arrive where the rest of your day arrives;
     · annonces — Portal management already publishes to the team;
     · administration, labels and trash — the Admin panel owns those.
   What stays here is the work that exists nowhere else. */

export function ActesView({ lang: portalLang, country = 'FR', onChrome }: { lang: Lang; country?: ActesCountry; onChrome?: (quiet: boolean) => void }) {
  /* FRANCE OR PORTUGAL (28 Oct 2026). One section, two countries: Portugal's
     is the lighter one — dossiers, requests, intake, guide and bin, no DPC,
     no Poste log, no authorities register — and it speaks English. */
  useActesCountry(country);
  const lang: Lang = country === 'PT' ? 'en' : portalLang;
  const [tab, setTab] = useState<ActesTab>('board');
  /* One dossier, opened from anywhere — the board, the list, a deadline. The
     tab bar stays put underneath, so leaving it is one click, not a back
     button people have to guess at. */
  const [openId, setOpenId] = useState<string | null>(null);
  /* A demande to open on arrival (from a notification): id + a stamp so the
     same one asked twice still opens. */
  const [openDemande, setOpenDemande] = useState<{ id: string; n: number } | null>(null);
  /* Which view the next panel should open on — set when a dashboard tile
     asks for a specific one ("latest records" means the scans, not the
     table). The key on the panel makes it honour a second request too. */
  const [landing, setLanding] = useState<'scans' | null>(null);
  const openDossier = (id: string) => { setOpenId(id); setTab('dossiers'); };
  /* Opening a dossier from somewhere already INSIDE the section — the
     "see the dossier" button on a just-created demande, for one. The focus
     store is only read on mount, so from in here it needs an event. */
  useEffect(() => {
    const go = (e: Event) => {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id;
      if (id) openDossier(id);
    };
    window.addEventListener('lexidy:actes-open-dossier', go);
    return () => window.removeEventListener('lexidy:actes-open-dossier', go);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* The portal's rail folds away ON ARRIVAL, and comes back on the way out.
     ONCE, on mount — the callback is an inline arrow, so a dependency on it
     re-ran this effect on every render and slammed the rail shut again half a
     second after anyone opened it. Folding it is a courtesy; refusing to let
     it open is not something anybody asked for. */
  const chrome = useRef(onChrome);
  chrome.current = onChrome;
  useEffect(() => {
    chrome.current?.(true);
    return () => chrome.current?.(false);
  }, []);

  /* Arriving from a case page — or a notification — open the dossier it
     named. Also WHILE ALREADY HERE: a bell click with Actes on screen used to
     be ignored, because this read the request only on mount (24 Sept). */
  useEffect(() => {
    const apply = () => {
      const { id, tab: want } = takeActesFocus();
      /* A demande named by a notification opens ITSELF, in Demandes. */
      if (id && want === 'demandes') { setTab('demandes'); setOpenDemande({ id, n: Date.now() }); }
      else if (id) { setOpenId(id); setTab('dossiers'); }
      else if (want) setTab(want as ActesTab);
    };
    apply();
    return onActesFocus(apply);
  }, []);

  const ref = useQuery<ActesReference>({
    queryKey: ['actes', 'reference'],
    queryFn: ({ signal }) => actesReference(signal),
    staleTime: 5 * 60_000,
    /* A DROPPED CONNECTION IS RETRIED, A REFUSAL IS NOT. «Section crashed:
       Actes — Could not reach the server» (28 and 29 Sept) was a laptop
       waking up at 08:00 with the network still coming back: one failed
       fetch and the whole section closed. Now a network failure (or a 5xx)
       is tried again three times, 1 s → 2 s → 4 s, and again as soon as the
       browser is back online; a 403 still answers at once. */
    retry: (n, err) => n < 3 && isTransient(err),
    retryDelay: (n) => Math.min(1000 * 2 ** n, 8000),
    refetchOnReconnect: true,
  });

  /* Les compteurs des onglets viennent du tableau de bord, avec la MÊME clé
     de cache : aucune requête de plus, c'est la réponse déjà en mémoire. */
  const board = useQuery<ActesBoard>({
    queryKey: ['actes', 'board', ref.data?.me.id],
    queryFn: ({ signal }) => actesBoard(signal),
    enabled: !!ref.data,
    staleTime: 60_000,
  });
  const counts = board.data?.counts;

  const tabs = useMemo(
    () => actesTabsFor(TABS, country, ref.data?.me),
    [country, ref.data?.me],
  );
  useEffect(() => {
    if (ref.data && !tabs.some((t) => t.id === tab)) setTab('dossiers');
  }, [ref.data, tabs, tab]);

  /* ── UNA SECCIÓN QUE NO ABRE SE REGISTRA COMO CAÍDA ──
     «The section could not open» se enseñaba y se anotaba como un error
     «handled» cualquiera: una línea, una persona, y el vigilante de errores
     no lo contaba. Una sección entera cerrada es lo mismo que una pantalla
     en blanco para quien la necesita. Se anota con el mismo nombre que la
     tarjeta roja, y así el cron avisa a la primera. (Un 403 de verdad —«no
     eres del equipo de Francia»— no es una caída y no se anota.)
     El hook va AQUÍ, encima de los `return` de abajo: los hooks nunca
     debajo de una puerta. */
  useEffect(() => {
    if (!ref.error) return;
    const msg = friendlyError(ref.error, lang);
    if (/réservé|reserved|403/i.test(msg)) return;
    void logPortalError({ kind: 'runtime', message: `Section crashed: Actes — ${msg}`, context: window.location.href }).catch(() => undefined);
  }, [ref.error, lang]);

  const L = (t: { fr: string; en: string }) => (lang === 'fr' ? t.fr : actesEn(t.en));

  if (ref.isLoading) {
    return <div className="actes"><p className="muted-note">{lang === 'fr' ? 'Chargement…' : 'Loading…'}</p></div>;
  }
  if (ref.error) {
    /* A refusal and a breakage are not the same thing, and saying "reserved to
       the French team" to a French super admin because a view failed to build
       is worse than saying nothing. The server answers 403 in its own words
       for a real refusal; anything else is a fault and reads as one. */
    const msg = friendlyError(ref.error, lang);
    const refused = /réservé|reserved|403/i.test(msg);
    return (
      <div className="actes">
        <div className="empty-state">
          <div className="ico">{refused ? '🔒' : '⚠'}</div>
          <h3>{refused
            ? (country === 'PT' ? 'Reserved for Portugal’s Immigration team'
              : lang === 'fr' ? 'Accès réservé à l’équipe France' : 'Reserved for the French team')
            : (lang === 'fr' ? 'La section n’a pas pu s’ouvrir' : 'The section could not open')}</h3>
          <p>{msg}</p>
          {!refused && (
            <button className="btn btn-sm btn-ghost" onClick={() => void ref.refetch()}>
              {lang === 'fr' ? 'Réessayer' : 'Try again'}
            </button>
          )}
        </div>
      </div>
    );
  }

  const data = ref.data as ActesReference;

  return (
    <div className="actes">
      {/* The section's own bar: what this is, and its pages. It scrolls
          sideways on a phone rather than wrapping into three crowded rows. */}
      <header>
        <div className="head">
          <div className="brand">
            {/* Le drapeau de la maquette : trois bandes, pas un émoji. */}
            {country === 'PT' ? (
              <span className="flag" aria-hidden="true">
                <i style={{ background: '#046A38', flex: 2 }} /><i style={{ background: '#DA291C', flex: 3 }} />
              </span>
            ) : (
              <span className="flag" aria-hidden="true">
                <i style={{ background: '#26307A' }} /><i style={{ background: '#fff' }} /><i style={{ background: '#C0543A' }} />
              </span>
            )}
            <span>
              <h1>{lang === 'fr' ? 'Gestion des Actes' : 'Civil records'}</h1>
              <span>{country === 'PT' ? 'Portugal · Nationality' : lang === 'fr' ? 'Nationalité & passeport' : 'Nationality & passport'}</span>
            </span>
          </div>
        {/* Le réglage des mails vivait ici. Il est parti dans la cloche des
            notifications, sous « France · Actes » : il gouverne une boîte
            mail, pas cette section, et le garder aux deux endroits aurait
            fait deux réglages pour une seule valeur. */}
          <nav className="tabs" aria-label={lang === 'fr' ? 'Sections' : 'Sections'}>
            {tabs.map((t) => {
              /* LE COMPTEUR SUR L'ONGLET, COMME DANS LA MAQUETTE.
                 Trois onglets le portent, et chacun est un chiffre qui existe
                 déjà sur le tableau de bord — mes dossiers, mes demandes, les
                 DPC à échéance. On ne calcule rien de neuf et on n'affiche
                 rien tant que le chiffre n'est pas connu. */
              /* DOS CONTADORES, NO TRES, Y NINGUNO EN ROJO.
                 El de DPC iba en la pastilla llena —la de color— y se leía
                 como una alarma: dieciséis expedientes con plazo son trabajo
                 normal, no una emergencia. Fuera el número de DPC, y los dos
                 que quedan —mis dossiers y mis demandes— en la pastilla
                 discreta: son un dato, no un aviso. */
              const badge = t.id === 'dossiers' ? counts?.mes_dossiers
                : t.id === 'demandes' ? counts?.mes_actes
                  : undefined;
              const nb = badge === undefined ? null : Number(badge);
              return (
                <button
                  key={t.id}
                  className="tab"
                  aria-current={tab === t.id ? 'page' : undefined}
                  onClick={() => { setTab(t.id); if (t.id !== 'dossiers') setOpenId(null); }}
                >
                  <KBIcon name={t.icon} size={14} />
                  {L(t)}
                  {nb ? <span className="badge q">{nb}</span> : null}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="head-rule" />
      </header>

      <div className="actes-body">
        {tab === 'board' && <BoardPanel lang={lang} reference={data} onOpenDossier={openDossier} onGo={(t, view) => { setOpenId(null); setLanding(view ?? null); setTab(t); }} />}
        {tab === 'dossiers' && (openId
          ? <DossierDetail lang={lang} reference={data} id={openId} onBack={() => setOpenId(null)} />
          : <DossiersPanel lang={lang} reference={data} onOpen={openDossier} />)}
        {tab === 'demandes' && <DemandesPanel key={landing ?? 'table'} lang={lang} reference={data} onOpenDossier={openDossier} landing={landing ?? undefined} openActe={openDemande} />}
        {tab === 'reception' && <ReceptionPanel lang={lang} reference={data} />}
        {tab === 'dpc' && <DpcPanel lang={lang} reference={data} onOpenDossier={openDossier} />}
        {tab === 'authorities' && <AuthoritiesPanel lang={lang} reference={data} />}
        {tab === 'guide' && <GuidePanel lang={lang} reference={data} />}
        {tab === 'trash' && <TrashPanel lang={lang} reference={data} />}
        {tab !== 'board' && tab !== 'dossiers' && tab !== 'demandes' && tab !== 'reception' && tab !== 'dpc' && tab !== 'authorities' && tab !== 'guide' && tab !== 'trash' && (
          <div className="empty-state">
            <div className="ico">🚧</div>
            <h3>{L(tabs.find((t) => t.id === tab) ?? { fr: '', en: '' })}</h3>
            <p>{lang === 'fr'
              ? 'Cette page arrive avec la suite de la migration.'
              : 'This page lands with the rest of the migration.'}</p>
          </div>
        )}
      </div>

      {/* « Annuler » — mounted once, listens for anything that offers a way back. */}
      <UndoToaster lang={lang} />
    </div>
  );
}


